<template>
  <div class="bs" :class="[`bs-${size}`, `bs-${variant}`, { 'bs-open': open, 'bs-disabled': disabled, 'bs-invalid': invalid, 'bs-block': block }]">
    <button
      :id="id"
      ref="trigger"
      type="button"
      class="bs-trigger"
      :class="triggerClass"
      :disabled="disabled"
      :aria-label="ariaLabel || undefined"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="open ? listId : undefined"
      @click.stop="toggle"
      @keydown="onTriggerKeydown"
    >
      <!-- #trigger replaces the shown value, e.g. a table cell that keeps
           its own look and only gains the menu. -->
      <slot name="trigger" :selected="selectedItem">
        <span class="bs-value-wrap">
          <span v-if="selectedItem && selectedItem.color" class="bs-dot" :style="{ background: selectedItem.color }" aria-hidden="true"></span>
          <span v-if="selectedItem && selectedItem.avatar" class="bs-avatar">{{ selectedItem.avatar }}</span>
          <span class="bs-value" :class="{ 'bs-placeholder': !selectedItem }">
            {{ selectedItem ? selectedItem.label : placeholder }}
          </span>
        </span>
      </slot>
      <svg class="bs-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>

    <!-- Rendered on <body> so no drawer, modal or scrolling table can clip it. -->
    <teleport to="body">
      <transition name="bs-menu">
        <div v-if="open" ref="menu" tabindex="-1" class="bs-menu" :class="{ 'bs-menu-up': placeUp }" :style="menuStyle"
          @mousedown.stop @keydown="onMenuKeydown">
          <div v-if="isSearchable" class="bs-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              aria-hidden="true">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input ref="search" v-model="query" type="text" :placeholder="searchPlaceholder"
              :aria-label="searchPlaceholder" :aria-controls="listId" />
          </div>
          <ul :id="listId" ref="list" class="bs-list" role="listbox" :aria-label="ariaLabel || placeholder">
            <li v-for="(item, i) in filteredItems" :key="item.key" role="option"
              class="bs-option"
              :class="{ 'bs-selected': isSelected(item), 'bs-active': i === activeIndex, 'bs-option-disabled': item.disabled }"
              :aria-selected="isSelected(item) ? 'true' : 'false'"
              :aria-disabled="item.disabled ? 'true' : undefined"
              @mouseenter="activeIndex = i"
              @click="choose(item)">
              <span v-if="item.color" class="bs-dot" :style="{ background: item.color }" aria-hidden="true"></span>
              <span v-if="item.avatar" class="bs-avatar">{{ item.avatar }}</span>
              <span class="bs-option-text">
                <span class="bs-option-label">{{ item.label }}</span>
                <span v-if="item.description" class="bs-option-desc">{{ item.description }}</span>
              </span>
              <svg class="bs-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </li>
            <li v-if="!filteredItems.length" class="bs-empty">{{ query ? noResultsText : emptyText }}</li>
          </ul>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
// One dropdown for the whole app, replacing the native <select>.
//
// Used with v-model like a select. The value emitted is the option's own value,
// untouched — a numeric id stays a number — so callers need no Number()/String()
// round trip. It is controlled: it always shows `modelValue`, so a parent that
// refuses a change (a failed save) simply does not update it and the dropdown
// shows the old value again, with no DOM to reset by hand.
//
// Options are either plain values ('High') or objects; `optionLabel`,
// `optionValue` and `optionDescription` name the object fields to read. An
// object may also carry `color` (a dot before the label, e.g. priority) or
// `avatar` (initials in a circle, e.g. people).

let uid = 0;

// null and undefined are the same "nothing chosen"; otherwise compare as text,
// so an id that arrives as '12' in one place and 12 in another still matches.
const sameValue = (a, b) => {
  if (a == null || b == null) return a == null && b == null;
  return a === b || String(a) === String(b);
};

const MENU_MAX_HEIGHT = 300;
const GAP = 6;

export default {
  name: 'BaseSelect',
  props: {
    modelValue: { type: null, default: null },
    options: { type: Array, default: () => [] },
    optionLabel: { type: String, default: 'label' },
    optionValue: { type: String, default: 'value' },
    optionDescription: { type: String, default: 'description' },
    optionDisabled: { type: String, default: 'disabled' },
    placeholder: { type: String, default: 'Select…' },
    // null = decide by size: a list longer than this gets a search box.
    searchable: { type: Boolean, default: null },
    searchThreshold: { type: Number, default: 8 },
    searchPlaceholder: { type: String, default: 'Search…' },
    emptyText: { type: String, default: 'No options' },
    noResultsText: { type: String, default: 'No matches' },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
    // md: forms and toolbars. sm: filters and dense rows.
    size: { type: String, default: 'md' },
    // default: bordered field. pill: a coloured badge (status pickers), whose
    // colours come from `triggerClass`. ghost: no frame until hovered, for a
    // value in a table row that can also be changed there.
    variant: { type: String, default: 'default' },
    triggerClass: { type: [String, Array, Object], default: '' },
    // Stretch to the container's width, as a form field does.
    block: { type: Boolean, default: false },
    ariaLabel: { type: String, default: '' },
    id: { type: String, default: undefined },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    uid += 1;
    return {
      open: false,
      query: '',
      activeIndex: -1,
      placeUp: false,
      menuStyle: {},
      listId: `bs-list-${uid}`,
    };
  },
  computed: {
    items() {
      return this.options.map((o, i) => {
        const isObj = o !== null && typeof o === 'object';
        const value = isObj ? o[this.optionValue] : o;
        return {
          key: `${i}-${value}`,
          value,
          label: String(isObj ? (o[this.optionLabel] ?? value ?? '') : (o ?? '')),
          description: isObj ? o[this.optionDescription] : undefined,
          disabled: isObj ? Boolean(o[this.optionDisabled]) : false,
          color: isObj ? o.color : undefined,
          avatar: isObj ? o.avatar : undefined,
        };
      });
    },
    selectedItem() {
      return this.items.find(it => sameValue(it.value, this.modelValue)) || null;
    },
    isSearchable() {
      return this.searchable === null ? this.items.length > this.searchThreshold : this.searchable;
    },
    filteredItems() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.items;
      return this.items.filter(it =>
        it.label.toLowerCase().includes(q) ||
        String(it.description || '').toLowerCase().includes(q));
    },
  },
  watch: {
    query() {
      this.activeIndex = this.firstEnabled(0, 1);
    },
  },
  beforeUnmount() {
    this.unbind();
  },
  methods: {
    isSelected(item) {
      return sameValue(item.value, this.modelValue);
    },
    toggle() {
      if (this.open) this.close();
      else this.openMenu();
    },
    async openMenu() {
      if (this.disabled || this.open) return;
      this.query = '';
      this.open = true;
      const sel = this.filteredItems.findIndex(it => this.isSelected(it));
      this.activeIndex = sel >= 0 ? sel : this.firstEnabled(0, 1);
      this.position();
      this.bind();
      await this.$nextTick();
      this.position();
      if (this.isSearchable) this.$refs.search?.focus();
      else this.$refs.menu?.focus?.();
      this.scrollActiveIntoView();
    },
    close(refocus = false) {
      if (!this.open) return;
      this.open = false;
      this.unbind();
      if (refocus) this.$refs.trigger?.focus();
    },
    choose(item) {
      if (!item || item.disabled) return;
      this.close(true);
      if (sameValue(item.value, this.modelValue)) return;
      this.$emit('update:modelValue', item.value);
      this.$emit('change', item.value);
    },
    // Fixed to the viewport, beside the trigger; flips above it when there is
    // not room below, and never narrower than the trigger.
    position() {
      const t = this.$refs.trigger;
      if (!t) return;
      const r = t.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - GAP;
      const above = r.top - GAP;
      this.placeUp = below < Math.min(MENU_MAX_HEIGHT, 200) && above > below;
      const width = Math.max(r.width, 200);
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      const style = {
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${Math.max(140, Math.min(MENU_MAX_HEIGHT, this.placeUp ? above : below))}px`,
      };
      if (this.placeUp) style.bottom = `${window.innerHeight - r.top + GAP}px`;
      else style.top = `${r.bottom + GAP}px`;
      this.menuStyle = style;
    },
    bind() {
      this._outside = (e) => {
        if (this.$el.contains(e.target) || this.$refs.menu?.contains(e.target)) return;
        this.close();
      };
      this._reposition = () => this.position();
      document.addEventListener('mousedown', this._outside, true);
      window.addEventListener('scroll', this._reposition, true);
      window.addEventListener('resize', this._reposition);
    },
    unbind() {
      if (this._outside) document.removeEventListener('mousedown', this._outside, true);
      if (this._reposition) {
        window.removeEventListener('scroll', this._reposition, true);
        window.removeEventListener('resize', this._reposition);
      }
      this._outside = null;
      this._reposition = null;
    },
    firstEnabled(from, step) {
      const list = this.filteredItems;
      for (let i = from; i >= 0 && i < list.length; i += step) {
        if (!list[i].disabled) return i;
      }
      return -1;
    },
    move(step) {
      const list = this.filteredItems;
      if (!list.length) return;
      let i = this.activeIndex;
      for (let n = 0; n < list.length; n += 1) {
        i = (i + step + list.length) % list.length;
        if (!list[i].disabled) break;
      }
      this.activeIndex = i;
      this.scrollActiveIntoView();
    },
    scrollActiveIntoView() {
      this.$nextTick(() => {
        const el = this.$refs.list?.children?.[this.activeIndex];
        el?.scrollIntoView?.({ block: 'nearest' });
      });
    },
    onTriggerKeydown(e) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        this.openMenu();
      }
    },
    onMenuKeydown(e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); this.move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.move(-1); }
      else if (e.key === 'Enter') { e.preventDefault(); this.choose(this.filteredItems[this.activeIndex]); }
      else if (e.key === 'Escape') { e.preventDefault(); this.close(true); }
      else if (e.key === 'Tab') { this.close(); }
    },
  },
};
</script>

<style scoped>
.bs {
  position: relative;
  display: inline-flex;
  min-width: 0;
  font-family: inherit;
}

.bs-block {
  display: flex;
  width: 100%;
}

.bs-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  width: 100%;
  min-width: 0;
  height: 42px;
  padding: 0 0.9rem 0 1rem;
  background: var(--color-surface);
  border: 1.5px solid var(--color-border);
  border-radius: 12px;
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-base);
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.bs-trigger:hover:not(:disabled) {
  border-color: var(--primary-400, var(--primary-400));
}

.bs-trigger:focus-visible,
.bs-open .bs-trigger {
  outline: none;
  border-color: var(--primary-500, var(--primary-500));
  box-shadow: 0 0 0 4px rgba(0, 132, 255, 0.12);
}

.bs-invalid .bs-trigger {
  border-color: var(--error-500);
}

.bs-invalid .bs-trigger:focus-visible,
.bs-invalid.bs-open .bs-trigger {
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}

.bs-disabled .bs-trigger {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--color-surface-subtle);
}

.bs-value-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.bs-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bs-placeholder {
  color: var(--color-text-muted);
  font-weight: 400;
}

.bs-arrow {
  flex-shrink: 0;
  color: var(--color-text-muted);
  transition: transform 0.2s ease;
}

.bs-open .bs-arrow {
  transform: rotate(180deg);
}

/* Ghost: reads as plain content; the frame appears on hover and focus. A
   locked one keeps full colour — it is still the row's data — and drops the
   arrow so it does not look clickable. */
.bs-ghost {
  min-width: 0;
  max-width: 100%;
}

.bs-ghost .bs-trigger {
  width: auto;
  max-width: 100%;
  height: auto;
  justify-content: flex-start;
  gap: 0.4rem;
  margin-left: -0.45rem;
  padding: 0.3rem 0.45rem;
  border-color: transparent;
  border-radius: 10px;
  background: transparent;
}

.bs-ghost .bs-trigger:hover:not(:disabled) {
  border-color: var(--color-border);
  background: var(--color-surface-subtle);
}

.bs-ghost.bs-disabled .bs-trigger {
  opacity: 1;
  background: transparent;
  cursor: default;
}

.bs-ghost.bs-disabled .bs-arrow {
  display: none;
}

/* Small: filters, pagers and dense table rows. */
.bs-sm .bs-trigger {
  height: 34px;
  padding: 0 0.7rem 0 0.8rem;
  border-radius: 9px;
  font-size: var(--text-md);
}

</style>

<!-- Unscoped: the menu is teleported to <body>, outside this component's
     scoped subtree. Every selector is prefixed bs- so nothing leaks. -->
<style>
/* Pill: a status badge that opens a menu. Kept here rather than in the scoped
   block because its tint comes from a class the caller passes onto the button
   (the app-wide badge-* status classes), and the two have to be ranked against
   each other on purpose: base pill < status tint. */
.bs.bs-pill > .bs-trigger {
  width: auto;
  height: 30px;
  padding: 0 0.65rem 0 0.85rem;
  border: 1.5px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-surface-muted);
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  font-weight: 700;
}

.bs.bs-pill > .bs-trigger .bs-arrow {
  color: currentColor;
  opacity: 0.75;
}

.bs.bs-pill.bs-open > .bs-trigger,
.bs.bs-pill > .bs-trigger:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 132, 255, 0.18);
}

.bs.bs-pill > .bs-trigger:hover:not(:disabled) {
  filter: brightness(0.96);
}

.bs.bs-pill > .bs-trigger.badge-open { background: var(--color-surface-muted); color: var(--color-text-secondary); border-color: var(--color-border); }
.bs.bs-pill > .bs-trigger.badge-in-progress { background: var(--primary-50); color: var(--info-600); border-color: var(--primary-100); }
.bs.bs-pill > .bs-trigger.badge-ready-for-review { background: var(--warning-100); color: var(--warning-700); border-color: var(--amber-100); }
.bs.bs-pill > .bs-trigger.badge-completed { background: var(--emerald-50); color: var(--emerald-700); border-color: var(--emerald-100); }
.bs.bs-pill > .bs-trigger.badge-drawing-submission { background: var(--sky-50); color: var(--sky-700); border-color: var(--sky-100); }
.bs.bs-pill > .bs-trigger.badge-client-review { background: var(--violet-50); color: var(--violet-600); border-color: var(--violet-100); }
.bs.bs-pill > .bs-trigger.badge-closed { background: var(--fixed-gray-800); color: #fff; border-color: var(--gray-900); }
.bs.bs-pill > .bs-trigger.badge-blocker { background: var(--error-50); color: var(--error-700); border-color: var(--red-100); }
/* A pipeline-stage picker: purple, so it is never read as a second status. */
.bs.bs-pill > .bs-trigger.bs-tone-stage { background: var(--violet-100); color: var(--violet-700); border-color: var(--violet-200); }

.bs-menu {
  position: fixed;
  z-index: 10050;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 14px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.14);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  outline: none;
  overflow: hidden;
}

.bs-search {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-shrink: 0;
  height: 40px;
  margin-bottom: 0.4rem;
  padding: 0 0.8rem;
  background: var(--color-surface-subtle);
  border-radius: 10px;
  color: var(--color-text-secondary);
}

.bs-search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-text);
}

.bs-search input::placeholder {
  color: var(--color-text-muted);
}

.bs-list {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
}

.bs-option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 38px;
  padding: 0.45rem 0.75rem;
  border-radius: 9px;
  cursor: pointer;
  color: var(--color-text);
  font-size: var(--text-base);
  transition: background 0.12s ease;
}

.bs-option.bs-active {
  background: var(--color-surface-subtle);
}

.bs-option.bs-selected {
  background: var(--primary-50);
  color: var(--primary-600);
  font-weight: 600;
}

.bs-option-disabled {
  cursor: default;
  color: var(--color-icon-muted);
}

.bs-option-disabled.bs-active {
  background: transparent;
}

.bs-dot {
  flex-shrink: 0;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.bs-avatar {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--blue-50);
  color: var(--primary-700);
  font-size: var(--text-xs);
  font-weight: 700;
}

.bs-option-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.bs-option-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bs-option-desc {
  margin-top: 1px;
  font-size: var(--text-sm);
  font-weight: 400;
  color: var(--color-text-muted);
}

.bs-check {
  flex-shrink: 0;
  color: var(--primary-500);
  opacity: 0;
}

.bs-selected .bs-check {
  opacity: 1;
}

.bs-empty {
  padding: 1rem;
  text-align: center;
  font-size: var(--text-base);
  color: var(--color-text-muted);
}

.bs-menu-enter-active,
.bs-menu-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.bs-menu-enter-from,
.bs-menu-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.bs-menu-up.bs-menu-enter-from,
.bs-menu-up.bs-menu-leave-to {
  transform: translateY(6px);
}
</style>
